import React from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { adminForumListPosts, adminForumCreatePost, adminForumDeletePost, adminForumUsers, adminForumUserActivity } from "../../lib/api";

const TOPICS = [
  { slug: "recovery_help", label: "Recovery Help" },
  { slug: "lifestyle", label: "Lifestyle" },
  { slug: "hobbies", label: "Hobbies" },
  { slug: "social_life", label: "Social Life" },
  { slug: "updates", label: "Updates" },
  { slug: "resources", label: "Resources" },
  { slug: "bug_reports", label: "Bug Reports" },
];

export default function AdminForum() {
  const [params] = useSearchParams();
  const passcode = params.get("passcode") || "";
  const [posts, setPosts] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [selectedUserId, setSelectedUserId] = React.useState("");
  const [activity, setActivity] = React.useState([]);
  const [postSearch, setPostSearch] = React.useState("");
  const [topicFilter, setTopicFilter] = React.useState("");
  const [userSearch, setUserSearch] = React.useState("");
  const [newPost, setNewPost] = React.useState({ topic: "updates", title: "", body: "" });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    if (!passcode) return;
    setLoading(true);
    try {
      const [p, u] = await Promise.all([
        adminForumListPosts({ passcode, topic: topicFilter, search: postSearch, page: 1, pageSize: 30 }),
        adminForumUsers({ passcode, search: userSearch, page: 1, pageSize: 30 }),
      ]);
      const pItems = Array.isArray(p?.items) ? p.items : [];
      const uItems = Array.isArray(u?.items) ? u.items : [];
      setPosts(pItems);
      setUsers(uItems);
      if (!selectedUserId && uItems[0]?.id) setSelectedUserId(uItems[0].id);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load forum admin data.");
    } finally {
      setLoading(false);
    }
  }, [passcode, topicFilter, postSearch, userSearch, selectedUserId]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    const run = async () => {
      if (!passcode || !selectedUserId) {
        setActivity([]);
        return;
      }
      try {
        const res = await adminForumUserActivity(selectedUserId, { passcode, page: 1, pageSize: 80 });
        setActivity(Array.isArray(res?.items) ? res.items : []);
      } catch {
        setActivity([]);
      }
    };
    run();
  }, [passcode, selectedUserId]);

  const onCreateUpdate = async (e) => {
    e.preventDefault();
    try {
      await adminForumCreatePost(newPost, passcode);
      setNewPost((s) => ({ ...s, title: "", body: "" }));
      await load();
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Could not create post.");
    }
  };

  const onDeletePost = async (postId) => {
    if (!window.confirm("Delete this post and all comments?")) return;
    try {
      await adminForumDeletePost(postId, passcode);
      await load();
    } catch (e3) {
      setError(e3?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:py-10 space-y-6" data-testid="admin-forum-page">
      <Card>
        <CardHeader>
          <CardTitle data-testid="admin-forum-title">Forum Admin</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!passcode && <div className="text-sm text-slate-500">Missing passcode. Use /admin first.</div>}
          {!!error && <div className="text-sm text-red-600" data-testid="admin-forum-error">{error}</div>}
          {loading && <div className="text-sm text-slate-500">Loading…</div>}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="admin-forum-top-grid">
            <div className="rounded-xl border p-3" data-testid="admin-forum-create-post-card">
              <div className="font-semibold text-sm mb-2">Create Post (Admin)</div>
              <form onSubmit={onCreateUpdate} className="space-y-2" data-testid="admin-forum-create-post-form">
                <select value={newPost.topic} onChange={(e) => setNewPost((s) => ({ ...s, topic: e.target.value }))} className="w-full rounded-md border px-3 py-2 text-sm" data-testid="admin-forum-create-topic-select">
                  {TOPICS.map((t) => <option key={t.slug} value={t.slug}>{t.label}</option>)}
                </select>
                <input value={newPost.title} onChange={(e) => setNewPost((s) => ({ ...s, title: e.target.value }))} className="w-full rounded-md border px-3 py-2 text-sm" placeholder="Title" data-testid="admin-forum-create-title-input" />
                <textarea value={newPost.body} onChange={(e) => setNewPost((s) => ({ ...s, body: e.target.value }))} className="w-full min-h-[90px] rounded-md border px-3 py-2 text-sm" placeholder="Body" data-testid="admin-forum-create-body-input" />
                <div className="flex justify-end"><Button type="submit" data-testid="admin-forum-create-submit-button">Publish</Button></div>
              </form>
            </div>

            <div className="rounded-xl border p-3" data-testid="admin-forum-posts-card">
              <div className="font-semibold text-sm mb-2">Moderate Posts</div>
              <div className="flex gap-2 mb-2">
                <input value={postSearch} onChange={(e) => setPostSearch(e.target.value)} className="w-full rounded-md border px-3 py-2 text-sm" placeholder="Search posts" data-testid="admin-forum-post-search-input" />
                <select value={topicFilter} onChange={(e) => setTopicFilter(e.target.value)} className="rounded-md border px-2 py-2 text-sm" data-testid="admin-forum-post-topic-filter">
                  <option value="">All</option>
                  {TOPICS.map((t) => <option key={t.slug} value={t.slug}>{t.label}</option>)}
                </select>
              </div>
              <div className="max-h-[320px] overflow-auto space-y-2" data-testid="admin-forum-post-list">
                {posts.map((p) => (
                  <div key={p.id} className="rounded-lg border p-2" data-testid={`admin-forum-post-item-${p.id}`}>
                    <div className="text-sm font-medium">{p.title}</div>
                    <div className="text-xs text-slate-500">{p.topic} · u/{p.authorUsername}</div>
                    <div className="mt-2 flex justify-end">
                      <Button type="button" variant="destructive" onClick={() => onDeletePost(p.id)} data-testid={`admin-forum-delete-post-${p.id}`}>Delete</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[320px,1fr] gap-4" data-testid="admin-forum-users-grid">
            <div className="rounded-xl border p-3" data-testid="admin-forum-users-list-card">
              <div className="font-semibold text-sm mb-2">Users</div>
              <input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="w-full rounded-md border px-3 py-2 text-sm mb-2" placeholder="Search user" data-testid="admin-forum-user-search-input" />
              <div className="max-h-[360px] overflow-auto space-y-2" data-testid="admin-forum-user-list">
                {users.map((u) => (
                  <button key={u.id} className={`w-full text-left rounded-lg border p-2 ${selectedUserId === u.id ? "bg-emerald-50 border-emerald-400" : ""}`} onClick={() => setSelectedUserId(u.id)} data-testid={`admin-forum-user-item-${u.id}`}>
                    <div className="text-sm font-medium">{u.username}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                    <div className="text-[11px] text-slate-500 mt-1">Posts {u.postsCount} · Comments {u.commentsCount} · Votes {u.votesCount}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-xl border p-3" data-testid="admin-forum-user-activity-card">
              <div className="font-semibold text-sm mb-2">User Activity</div>
              {!selectedUserId && <div className="text-sm text-slate-500">Select a user to view activity.</div>}
              <div className="max-h-[360px] overflow-auto space-y-2" data-testid="admin-forum-user-activity-list">
                {activity.map((a) => (
                  <div key={a.id} className="rounded-md border p-2" data-testid={`admin-forum-user-activity-${a.id}`}>
                    <div className="text-sm font-medium">{a.action}</div>
                    <div className="text-xs text-slate-500">{new Date(a.createdAt).toLocaleString()} · ref: {a.refId}</div>
                    <div className="text-xs mt-1 text-slate-600">{JSON.stringify(a.meta || {})}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
